import React, { useState } from 'react';
import { Card, Row, Col, Button, Badge, Alert } from 'react-bootstrap';
import { FaFileAlt, FaFilePdf, FaFileImage, FaTrash, FaUpload, FaCheck } from 'react-icons/fa';
import { getDocumentCategories, validateDocumentFile, formatFileSize } from '../../../services/employeeDocumentAPI';

/**
 * EmployeeCreateModal için özel belge yükleme formu
 * API çağrısı yapmaz, sadece state management
 * 
 * Path: components/employee/EmployeeDocuments/EmployeeDocumentUploadForm.jsx
 */
const EmployeeDocumentUploadForm = ({ selectedDocuments, setSelectedDocuments }) => {
    const [uploadError, setUploadError] = useState('');
    const [categories] = useState(() => getDocumentCategories());

    // Dosya seçme handler'ı
    const handleFileSelect = (categoryId, event) => {
        const files = Array.from(event.target.files);
        setUploadError('');

        if (files.length === 0) return;

        const validFiles = [];

        files.forEach(file => {
            try {
                // Dosya validasyonu
                validateDocumentFile(file);
                validFiles.push(file);
            } catch (error) {
                setUploadError(error.message);
                return;
            }
        });

        if (validFiles.length > 0) {
            // Her dosya için document objesi oluştur
            const newDocuments = validFiles.map(file => ({
                id: `${categoryId}_${Date.now()}_${Math.random()}`,
                category: categoryId,
                file: file,
                originalName: file.name,
                size: file.size,
                type: file.type,
                uploadDate: new Date(),
                // Temp URL preview için
                previewUrl: URL.createObjectURL(file)
            }));

            setSelectedDocuments(prev => [...prev, ...newDocuments]);

            // Input'u temizle
            event.target.value = '';
        }
    };

    // Belge silme
    const handleRemoveDocument = (documentId) => {
        setSelectedDocuments(prev => {
            const updatedDocs = prev.filter(doc => doc.id !== documentId);

            // Memory leak önlemek için URL'i revoke et
            const removedDoc = prev.find(doc => doc.id === documentId);
            if (removedDoc && removedDoc.previewUrl) {
                URL.revokeObjectURL(removedDoc.previewUrl);
            }

            return updatedDocs;
        });
    };

    // Kategori için yüklenmiş belge sayısı
    const getDocumentCountByCategory = (categoryId) => {
        return selectedDocuments.filter(doc => doc.category === categoryId).length;
    };

    // Dosya icon'u
    const getFileIcon = (type) => {
        if (type === 'application/pdf') return FaFilePdf;
        if (type.startsWith('image/')) return FaFileImage;
        return FaFileAlt;
    };

    return (
        <Card className="mb-4">
            <Card.Header>
                <h6 className="mb-0">📎 Personel Belgeleri</h6>
                <small className="text-muted">
                    Gerekli evrakları kategorilere göre yükleyin
                </small>
            </Card.Header>
            <Card.Body>
                {uploadError && (
                    <Alert variant="danger" className="mb-3">
                        {uploadError}
                    </Alert>
                )}

                {/* Belge Kategorileri Grid */}
                <Row>
                    {categories.map(category => {
                        const IconComponent = FaFileAlt;
                        const documentCount = getDocumentCountByCategory(category.id);
                        const hasDocuments = documentCount > 0;

                        return (
                            <Col md={4} key={category.id} className="mb-3">
                                <div className={`border rounded p-3 text-center h-100 ${hasDocuments ? 'border-success bg-light' : 'border-secondary'}`}>
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <IconComponent
                                            className={`text-${category.color}`}
                                            size={20}
                                        />
                                        {category.required && (
                                            <Badge bg="danger" className="px-1" style={{ fontSize: '0.7em' }}>
                                                Zorunlu
                                            </Badge>
                                        )}
                                        {hasDocuments && (
                                            <Badge bg="success" className="px-1" style={{ fontSize: '0.7em' }}>
                                                <FaCheck size={10} />
                                            </Badge>
                                        )}
                                    </div>

                                    <h6 className="mb-2">{category.label}</h6>

                                    <div className="mb-3">
                                        <Badge bg={hasDocuments ? 'success' : 'secondary'}>
                                            {documentCount} belge
                                        </Badge>
                                    </div>

                                    {/* File input - hidden */}
                                    <input
                                        type="file"
                                        id={`file-${category.id}`}
                                        multiple={category.multiple}
                                        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                                        onChange={(e) => handleFileSelect(category.id, e)}
                                        style={{ display: 'none' }}
                                    />

                                    {/* Upload button */}
                                    <label
                                        htmlFor={`file-${category.id}`}
                                        className={`btn btn-outline-${category.color} btn-sm w-100 mb-2`}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <FaUpload className="me-1" />
                                        Yükle
                                    </label>

                                    {/* Yüklenen dosyalar listesi */}
                                    {selectedDocuments
                                        .filter(doc => doc.category === category.id)
                                        .map(doc => {
                                            const FileIcon = getFileIcon(doc.type);
                                            return (
                                                <div
                                                    key={doc.id}
                                                    className="d-flex justify-content-between align-items-center bg-white p-1 rounded mt-1 border"
                                                    style={{ fontSize: '0.8em' }}
                                                >
                                                    <div className="d-flex align-items-center text-truncate">
                                                        <FileIcon className="me-1 text-muted" size={12} />
                                                        <span className="text-truncate" style={{ maxWidth: '100px' }}>
                                                            {doc.originalName}
                                                        </span>
                                                    </div>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleRemoveDocument(doc.id)}
                                                        style={{ fontSize: '0.7em', padding: '2px 6px' }}
                                                    >
                                                        <FaTrash size={10} />
                                                    </Button>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            </Col>
                        );
                    })}
                </Row>

                {/* Özet bilgi */}
                {selectedDocuments.length > 0 && (
                    <div className="mt-4 p-3 bg-info text-white rounded">
                        <div className="row text-center">
                            <div className="col-md-3">
                                <strong>{selectedDocuments.length}</strong>
                                <small className="d-block">Toplam Belge</small>
                            </div>
                            <div className="col-md-3">
                                <strong>
                                    {categories.filter(cat => cat.required && getDocumentCountByCategory(cat.id) > 0).length}
                                    /{categories.filter(cat => cat.required).length}
                                </strong>
                                <small className="d-block">Zorunlu Tamamlandı</small>
                            </div>
                            <div className="col-md-3">
                                <strong>
                                    {(selectedDocuments.reduce((sum, doc) => sum + doc.size, 0) / (1024 * 1024)).toFixed(1)} MB
                                </strong>
                                <small className="d-block">Toplam Boyut</small>
                            </div>
                            <div className="col-md-3">
                                <strong>
                                    {categories.filter(cat => getDocumentCountByCategory(cat.id) > 0).length}
                                    /{categories.length}
                                </strong>
                                <small className="d-block">Kategori Dolu</small>
                            </div>
                        </div>
                    </div>
                )}

                <Alert variant="info" className="mt-3 mb-0">
                    <small>
                        <strong>💡 Belge Yükleme Kuralları:</strong>
                        <ul className="mb-0 mt-1" style={{ fontSize: '0.9em' }}>
                            <li>Maksimum dosya boyutu: 10MB</li>
                            <li>Desteklenen formatlar: PDF, JPG, PNG, GIF, WEBP</li>
                            <li>Kırmızı etiketli belgeler zorunludur</li>
                            <li>Belgeler personel oluşturulduktan sonra yüklenecektir</li>
                        </ul>
                    </small>
                </Alert>
            </Card.Body>
        </Card>
    );
};

export default EmployeeDocumentUploadForm;